import { useCallback, useMemo, useState } from "react";
import { useErrorTabCount } from "src/Hooks/useErrorTabCount";
import { OrderTablesTabIndex } from "../Order/OrderTablesBox/OrderResult";
import { INLegsSearchParam } from "../../Interfaces/nLegsInterfaces";
import NLegsOrderTable from "../../Tables/NLegs/Order/NLegsOrderTable";
import ErrorsTable from "../../Tables/Order/ErrorsTable/ErrorsTable";
import { getNLegsErrorsTable } from "../../Requests/nLegsRequests";
import NLegsTradeTable from "../../Tables/NLegs/Trade/NLegsTradeTable";
import TabBox from "../../Components/Tabbox/TabBox";
import SearchParamBox, { NLegsFilterMode } from "./SearchParamBox";
import AddOrderBox from "./AddOrderBox/AddOrderBox";
import { useAppDispatch, useAppSelector } from "../../App/hooks";
import Modal from "../../Components/Modal/Modal";
import EditOrder from "./EditOrder";
import { setNLegsEditFlag } from "../../Features/NLegs/nLegsSlice";
import Orderbook from "./Orderbook/Orderbook";

interface IProps {
  legCount: 2 | 3 | 4;
}

const NLegs = ({ legCount }: IProps) => {
  const [tabIndex, setTabIndex] = useState(OrderTablesTabIndex.OrderTable);

  const [tablesSearchParam, setTablesSearchParam] = useState<INLegsSearchParam>(
    {} as INLegsSearchParam
  );

  const activeSearchParam = useMemo(() => {
    if (tabIndex === OrderTablesTabIndex.OrderTable &&
        (!tablesSearchParam.optionOrderTypes?.length || tablesSearchParam.optionOrderTypes.length === legCount)) {
      return tablesSearchParam;
    }
    const { optionOrderTypes, offsettype, ...filters } = tablesSearchParam;
    return filters;
  }, [tabIndex, tablesSearchParam, legCount]);

  const editFlag = useAppSelector((store) => store.nLegs.editFlag);
  const dispatch = useAppDispatch();

  const { errorTabCount, errorTableRefreshFlag, errorTableAfterFetchHandler } =
    useErrorTabCount(
      `nLegs${legCount}`,
      tabIndex,
      OrderTablesTabIndex.ErrorsTable,
      (searchParam, offset, pageSize) =>
        getNLegsErrorsTable(searchParam, offset, pageSize, legCount)
    );

  const tablesTabsChildren = [
    { index: OrderTablesTabIndex.OrderTable, title: "سفارشات" },
    { index: OrderTablesTabIndex.TradeTable, title: "معاملات" },
    {
      index: OrderTablesTabIndex.ErrorsTable,
      title: "خطا",
      count: errorTabCount,
    },
  ];

  const searchParamChangeHandler = useCallback((name: string, value: any) => {
    setTablesSearchParam((previous) => ({ ...previous, [name]: value }));
  }, []);

  const getTabIndexTable = () => {
    switch (tabIndex) {
      case OrderTablesTabIndex.OrderTable:
        return (
          <NLegsOrderTable
            searchParam={activeSearchParam}
            legCount={legCount}
          />
        );

      case OrderTablesTabIndex.TradeTable:
        return (
          <NLegsTradeTable
            searchParam={activeSearchParam}
            legCount={legCount}
          />
        );
      case OrderTablesTabIndex.ErrorsTable:
        return (
          <ErrorsTable
            getData={(sp, offset, pageSize) =>
              getNLegsErrorsTable(sp, offset, pageSize, legCount)
            }
            searchParams={activeSearchParam}
            onFetchComplete={errorTableAfterFetchHandler}
            refreshFlag={errorTableRefreshFlag}
          />
        );
    }
  };

  const getFilterMode = (): NLegsFilterMode => {
    switch (tabIndex) {
      case OrderTablesTabIndex.TradeTable:
        return "trade";
      case OrderTablesTabIndex.ErrorsTable:
        return "errors";
      default:
        return "order";
    }
  };

  return (
    <div className="flex flex-row justify-start items-start gap-5 w-full pr-5 outline-none border-none">
      <AddOrderBox legCount={legCount} />
      {editFlag ? (
        <Modal title="ویرایش سفارش"
          UI={<EditOrder />}
          onClose={() => {
            dispatch(setNLegsEditFlag(false));
          }}
        />
      ) : (
        <></>
      )}
      <div className="flex flex-col justify-start items-start">
        <div className="flex flex-row justify-start items-start w-full gap-8">
          <Orderbook legCount={legCount} />
        </div>
        <div className="w-full pt-5">
          <div className="max-w-min m-auto">
            <div className="w-full flex flex-row justify-start items-center gap-3">
              <div className="max-w-min shrink-0">
                <TabBox
                  activeTab={tabIndex}
                  children={tablesTabsChildren}
                  onChange={(name, index) => setTabIndex(index)}
                />
              </div>
              <SearchParamBox
                onChange={searchParamChangeHandler}
                searchParam={activeSearchParam}
                legCount={legCount}
                mode={getFilterMode()}
              />
            </div>
            <div className="w-full mt-2">{getTabIndexTable()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NLegs;
